import { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ResponsiveContainer, PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, LineChart, Line, AreaChart, Area, Legend, ReferenceLine } from "recharts";
import { Upload, Sparkles, Wallet, TrendingUp, TrendingDown, PiggyBank, AlertTriangle, ArrowRight, Info, FileUp, ShieldCheck, Lightbulb } from "lucide-react";
import { useStore } from "../context/StoreContext";
import { computeMonths, sortedMonths, budgetModel, budgetVsActual, recommendations, forecast, goalProjection, catMapOf } from "../lib/finance";
import { fmt, fmt0, monthLabel } from "../lib/format";
import { PageHeader, Kpi, Section, NativeSelect, Banner, ChartTip, PALETTE, Empty } from "../components/common";
import { Button } from "../components/ui/button";
import { InterestAlerts } from "./Debt";
import { BillReminderBanner } from "../components/BillReminders";
import { MoveWelcome, MoveBanner } from "../components/MoveHelpers";
import { InstallGuide, BackupReminder } from "../components/HomeNudges";
import { SyncNudge } from "../components/DeviceSync";
import AccountantReminder, { accountantReminderYear } from "../components/AccountantReminder";
import { AccountantPackButton } from "../components/TaxPack";
import { MissingReceiptAlert, YearEndReminder } from "../components/ReceiptScan";
import { Progress } from "../components/ui/progress";

function Welcome() {
  const { loadSample } = useStore();
  const nav = useNavigate();
  const steps = ["Upload a PDF statement", "We read & categorize it", "Review anything uncertain", "Get your budget"];
  return (
    <div data-testid="welcome-screen">
    <MoveWelcome />
    <div className="grid lg:grid-cols-12 gap-6 items-stretch">
      <div className="lg:col-span-7 fade-up">
        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-primary">Private household budgeting</p>
        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight mt-3 leading-[1.05]">Turn a bank statement into a budget you understand.</h1>
        <p className="text-muted-foreground text-base sm:text-lg mt-4 max-w-xl">Upload one monthly PDF statement. We'll read it on this device, sort every transaction, and build a starting household budget in minutes — no bank logins, no accounting knowledge.</p>
        <div className="flex flex-wrap gap-3 mt-7">
          <Button size="lg" className="h-12 px-6 text-base rounded-full" onClick={() => nav("/upload")} data-testid="welcome-upload-button"><Upload className="h-5 w-5 mr-2" /> Upload my statement</Button>
          <Button size="lg" variant="outline" className="h-12 px-6 text-base rounded-full" onClick={loadSample} data-testid="welcome-sample-button"><Sparkles className="h-5 w-5 mr-2" /> Explore with sample data</Button>
        </div>
        <ol className="grid sm:grid-cols-4 gap-3 mt-10">
          {steps.map((s, i) => (
            <li key={s} className="rounded-xl border bg-card p-4">
              <span className="num text-primary text-sm font-semibold">0{i + 1}</span>
              <p className="text-sm font-medium mt-1">{s}</p>
            </li>
          ))}
        </ol>
        <p className="flex items-center gap-2 text-sm text-muted-foreground mt-6"><ShieldCheck className="h-4 w-4 text-income" /> Your statements never leave this device. No ads, no screen tracking, no AI services.</p>
      </div>
      <div className="lg:col-span-5 relative rounded-3xl overflow-hidden min-h-[320px] fade-up">
        <img src="/welcome.jpg" alt="Family planning their budget at home" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent" />
        <div className="absolute bottom-0 p-6 text-white">
          <p className="font-display text-2xl font-bold">Upload → Analyze → Budget → Review → Improve</p>
          <p className="text-white/80 text-sm mt-1">Works offline. Installable on Mac, iPhone and iPad.</p>
        </div>
      </div>
    </div>
    </div>
  );
}

export default function Dashboard() {
  const { state } = useStore();
  const [filters, setFilters] = useState({ accountId: "all", memberId: "all" });
  const months = useMemo(() => computeMonths(state, filters), [state, filters]);
  const keys = sortedMonths(months);
  const [sel, setSel] = useState(null);
  const month = sel && months[sel] ? sel : keys[keys.length - 1];
  const model = useMemo(() => budgetModel(state), [state]);
  const recs = useMemo(() => recommendations(state).filter((r) => r.kind !== "interest").slice(0, 3), [state]);
  const fc = useMemo(() => forecast(state, 90), [state]);
  const cats = catMapOf(state);

  if (!state.transactions.length) return <Welcome />;
  const m = months[month] || { income: 0, expense: 0, savings: 0, net: 0, remaining: 0, savingsRate: 0, byCat: {} };
  const flagged = state.transactions.filter((t) => t.flagged).length;
  const byCat = Object.entries(m.byCat).filter(([, v]) => v > 0).map(([id, v]) => ({ name: cats[id]?.name || id, value: Math.round(v * 100) / 100 })).sort((a, b) => b.value - a.value);
  const pie = byCat.length > 8 ? [...byCat.slice(0, 7), { name: "Everything else", value: byCat.slice(7).reduce((s, x) => s + x.value, 0) }] : byCat;
  const trend = keys.slice(-12).map((k) => ({ month: monthLabel(k, true), Income: months[k].income, Expenses: months[k].expense, Savings: months[k].savings, "Net cash flow": months[k].net }));
  const topIds = Object.entries(keys.slice(-6).reduce((acc, k) => { Object.entries(months[k].byCat).forEach(([c, v]) => (acc[c] = (acc[c] || 0) + v)); return acc; }, {})).sort((a, b) => b[1] - a[1]).slice(0, 5).map(([c]) => c);
  const trendCats = keys.slice(-12).map((k) => Object.fromEntries([["month", monthLabel(k, true)], ...topIds.map((c) => [cats[c]?.name || c, Math.round((months[k].byCat[c] || 0) * 100) / 100])]));
  const bva = budgetVsActual(state, month, model).slice(0, 8).map((r) => ({ name: r.name, Budget: r.available, Actual: r.actual }));
  const alerts = (state.alerts || []).map((a) => ({ ...a, spent: m.byCat[a.category] || 0 })).filter((a) => a.spent >= Number(a.amount));

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      <PageHeader title="Your household at a glance" subtitle={`${monthLabel(month)} · ${state.isSample ? "Sample data" : `${state.transactions.length} transactions across ${state.accounts.length} account(s)`}`}>
        <NativeSelect value={month} onChange={setSel} testid="dashboard-month-select">
          {[...keys].reverse().map((k) => <option key={k} value={k}>{monthLabel(k)}</option>)}
        </NativeSelect>
        <NativeSelect value={filters.accountId} onChange={(v) => setFilters({ ...filters, accountId: v })} testid="dashboard-account-filter">
          <option value="all">All accounts</option>
          {state.accounts.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
        </NativeSelect>
        {state.members.length > 1 && (
          <NativeSelect value={filters.memberId} onChange={(v) => setFilters({ ...filters, memberId: v })} testid="dashboard-member-filter">
            <option value="all">Everyone</option>
            {state.members.map((x) => <option key={x.id} value={x.id}>{x.name}</option>)}
          </NativeSelect>
        )}
      </PageHeader>

      <div className="space-y-3">
        {model.preliminary && <Banner icon={Info} testid="preliminary-banner" action={<Button size="sm" variant="outline" asChild><Link to="/budget">Review budget</Link></Button>}><b>Preliminary budget.</b> Based on {model.monthCount} month(s) of data. A single statement may not show every bill — add missing income or expenses on the Budget page, or import more statements.</Banner>}
        {flagged > 0 && <Banner tone="warning" icon={AlertTriangle} testid="flagged-banner" action={<Button size="sm" variant="outline" asChild><Link to="/transactions?flagged=1">Review</Link></Button>}><b>{flagged} transaction(s) need a quick look.</b> We weren't fully sure about the category or details.</Banner>}
        {fc.shortage && <Banner tone="danger" icon={AlertTriangle} testid="shortage-banner" action={<Button size="sm" variant="outline" asChild><Link to="/bills?tab=forecast">See forecast</Link></Button>}><b>Possible cash shortage around {fc.shortage.date}.</b> Projected balance {fmt(fc.shortage.balance)} (estimate).</Banner>}
        <MoveBanner />
        <SyncNudge />
        <AccountantReminder />
        <InstallGuide />
        <BackupReminder />
        <BillReminderBanner />
        <YearEndReminder extra={accountantReminderYear(state) && <AccountantPackButton year={accountantReminderYear(state)} size="sm" label="Create & send accountant pack" testid="accountant-reminder-send" />} />
        <MissingReceiptAlert />
        <InterestAlerts compact />
        {alerts.map((a) => <Banner key={a.id} tone="warning" icon={AlertTriangle} testid={`alert-${a.id}`}>Spending alert: {cats[a.category]?.name} reached {fmt(a.spent)} this month (your alert is {fmt(a.amount)}).</Banner>)}
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 sm:gap-4 stagger">
        <Kpi label="Total income" value={fmt0(m.income)} tone="income" icon={TrendingUp} testid="kpi-income" />
        <Kpi label="Total spending" value={fmt0(m.expense)} tone="expense" icon={TrendingDown} testid="kpi-spending" hint="Excludes transfers & savings" />
        <Kpi label="Remaining cash" value={fmt0(m.remaining)} icon={Wallet} testid="kpi-remaining" hint="Income − spending" />
        <Kpi label="Savings rate" value={`${m.savingsRate}%`} icon={PiggyBank} testid="kpi-savings-rate" hint={`${fmt0(m.savings)} saved`} />
        <Kpi label={m.net >= 0 ? "Surplus" : "Deficit"} value={fmt0(Math.abs(m.net))} tone={m.net >= 0 ? "income" : "expense"} testid="kpi-surplus" hint="After spending & savings" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        <Section title="Spending by category" subtitle={monthLabel(month)} className="lg:col-span-5" testid="chart-spending-pie">
          {pie.length ? (
            <div className="h-72">
              <ResponsiveContainer>
                <PieChart>
                  <Pie data={pie} dataKey="value" nameKey="name" innerRadius="55%" outerRadius="85%" paddingAngle={2}>
                    {pie.map((_, i) => <Cell key={i} fill={PALETTE[i % PALETTE.length]} />)}
                  </Pie>
                  <Tooltip content={<ChartTip />} />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          ) : <Empty title="No spending this month" />}
        </Section>
        <Section title="Income vs expenses" subtitle="By month" className="lg:col-span-7" testid="chart-income-expense">
          <div className="h-72">
            <ResponsiveContainer>
              <BarChart data={trend}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                <XAxis dataKey="month" tick={{ fontSize: 12 }} stroke="hsl(var(--muted-foreground))" />
                <YAxis tickFormatter={(v) => `$${Math.round(v / 1000)}k`} tick={{ fontSize: 12 }} stroke="hsl(var(--muted-foreground))" />
                <Tooltip content={<ChartTip />} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Bar dataKey="Income" fill="hsl(var(--income))" radius={[6, 6, 0, 0]} />
                <Bar dataKey="Expenses" fill="hsl(var(--expense))" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Section>
        <Section title="Monthly cash flow" subtitle="Income − spending − savings" className="lg:col-span-6" testid="chart-cashflow">
          <div className="h-64">
            <ResponsiveContainer>
              <LineChart data={trend}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                <XAxis dataKey="month" tick={{ fontSize: 12 }} stroke="hsl(var(--muted-foreground))" />
                <YAxis tick={{ fontSize: 12 }} stroke="hsl(var(--muted-foreground))" />
                <ReferenceLine y={0} stroke="hsl(var(--muted-foreground))" />
                <Tooltip content={<ChartTip />} />
                <Line type="monotone" dataKey="Net cash flow" stroke="#6366F1" strokeWidth={3} dot={{ r: 4 }} />
                <Line type="monotone" dataKey="Savings" stroke="#0D9488" strokeWidth={2} strokeDasharray="5 4" />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </Section>
        <Section title="Spending trends" subtitle="Your 5 biggest categories" className="lg:col-span-6" testid="chart-trends">
          <div className="h-64">
            <ResponsiveContainer>
              <AreaChart data={trendCats}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                <XAxis dataKey="month" tick={{ fontSize: 12 }} stroke="hsl(var(--muted-foreground))" />
                <YAxis tick={{ fontSize: 12 }} stroke="hsl(var(--muted-foreground))" />
                <Tooltip content={<ChartTip />} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                {topIds.map((c, i) => <Area key={c} type="monotone" dataKey={cats[c]?.name || c} stackId="1" stroke={PALETTE[i]} fill={PALETTE[i]} fillOpacity={0.35} />)}
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </Section>
        <Section title="Budget vs actual" subtitle={monthLabel(month)} className="lg:col-span-7" testid="chart-budget-actual" action={<Button variant="ghost" size="sm" asChild><Link to="/budget">Edit <ArrowRight className="h-4 w-4 ml-1" /></Link></Button>}>
          <div className="h-80">
            <ResponsiveContainer>
              <BarChart data={bva} layout="vertical" margin={{ left: 20 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" horizontal={false} />
                <XAxis type="number" tick={{ fontSize: 12 }} stroke="hsl(var(--muted-foreground))" />
                <YAxis type="category" dataKey="name" width={130} tick={{ fontSize: 12 }} stroke="hsl(var(--muted-foreground))" />
                <Tooltip content={<ChartTip />} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Bar dataKey="Budget" fill="hsl(var(--muted-foreground) / 0.35)" radius={[0, 4, 4, 0]} />
                <Bar dataKey="Actual" fill="#0D9488" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Section>
        <div className="lg:col-span-5 space-y-6">
          <Section title="Largest expense categories" testid="largest-categories">
            <ul className="space-y-3">
              {byCat.slice(0, 5).map((c, i) => (
                <li key={c.name}>
                  <div className="flex justify-between text-sm"><span>{c.name}</span><span className="num font-medium">{fmt(c.value)}</span></div>
                  <div className="h-2 rounded-full bg-secondary mt-1.5 overflow-hidden"><div className="h-full rounded-full" style={{ width: `${(c.value / byCat[0].value) * 100}%`, background: PALETTE[i] }} /></div>
                </li>
              ))}
            </ul>
          </Section>
          <Section title="Savings progress" testid="savings-progress" action={<Button variant="ghost" size="sm" asChild><Link to="/goals">Goals <ArrowRight className="h-4 w-4 ml-1" /></Link></Button>}>
            {state.goals.length ? state.goals.slice(0, 4).map((g) => {
              const p = goalProjection(g);
              return (
                <div key={g.id} className="mb-3">
                  <div className="flex justify-between text-sm"><span>{g.name}</span><span className="num text-muted-foreground">{fmt0(g.current)} / {fmt0(g.target)}</span></div>
                  <Progress value={p.pct} className="h-2 mt-1.5" />
                </div>
              );
            }) : <p className="text-sm text-muted-foreground">No goals yet. <Link to="/goals" className="text-primary underline">Create one</Link>.</p>}
          </Section>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        <Section title="Suggestions" subtitle="Based only on your data" className="lg:col-span-8" testid="dashboard-recommendations" action={<Button variant="ghost" size="sm" asChild><Link to="/reports">All <ArrowRight className="h-4 w-4 ml-1" /></Link></Button>}>
          {recs.length ? (
            <ul className="space-y-3">
              {recs.map((r) => (
                <li key={r.id} className="flex gap-3 rounded-xl border p-3.5">
                  <Lightbulb className={`h-5 w-5 shrink-0 ${r.severity === "high" ? "text-expense" : r.severity === "medium" ? "text-warning" : "text-primary"}`} />
                  <div><p className="font-semibold text-sm">{r.title}</p><p className="text-sm text-muted-foreground">{r.text}</p></div>
                </li>
              ))}
            </ul>
          ) : <p className="text-sm text-muted-foreground">No suggestions right now — keep importing statements for better insights.</p>}
        </Section>
        <Section title="Next 30 days" subtitle="Estimate" className="lg:col-span-4" testid="dashboard-forecast">
          {fc.windows[0] && (
            <dl className="space-y-2 text-sm">
              <div className="flex justify-between"><dt>Expected income</dt><dd className="num text-income">{fmt(fc.windows[0].income)}</dd></div>
              <div className="flex justify-between"><dt>Upcoming bills</dt><dd className="num text-expense">{fmt(fc.windows[0].bills)}</dd></div>
              <div className="flex justify-between"><dt>Everyday spending</dt><dd className="num text-expense">{fmt(fc.windows[0].variable)}</dd></div>
              <div className="flex justify-between border-t pt-2 font-semibold"><dt>Projected balance</dt><dd className="num">{fc.balanceKnown ? fmt(fc.windows[0].endBalance) : "Set balance"}</dd></div>
            </dl>
          )}
          <Button variant="outline" size="sm" className="mt-4 w-full" asChild><Link to="/bills?tab=forecast">Open forecast</Link></Button>
          <Button variant="ghost" size="sm" className="mt-2 w-full" asChild><Link to="/upload"><FileUp className="h-4 w-4 mr-1" /> Import another statement</Link></Button>
        </Section>
      </div>
    </div>
  );
}
